import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { IMAGES, SITE } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";
import { usePageMeta } from "@/lib/usePageMeta";

export default function About() {
  const { t } = useLang();
  usePageMeta(t("meta.about")[0], t("meta.about")[1]);
  return (
    <>
      <PageHero
        overline={t("aboutPage.heroOverline")}
        title={t("aboutPage.heroTitle")}
        sub={t("aboutPage.heroSub")}
        testId="about"
      />

      <section className="bg-white py-16 sm:py-24" aria-labelledby="about-story-heading" data-testid="about-story-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.6fr] lg:gap-16 lg:px-8">
          <Reveal>
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full border-2 border-[#0F4C5C]/20" aria-hidden="true" />
              <img
                src={IMAGES.aboutTeam}
                alt={t("aboutPage.portraitAlt")}
                className="relative mx-auto w-full max-w-md object-cover shadow-xl"
                loading="lazy"
                data-testid="about-armando-image"
              />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="overline-label" data-testid="meet-armando-overline">{t("aboutPage.meetOverline")}</p>
            </Reveal>
            <Reveal delay={1}>
              <h2 id="about-story-heading" className="font-heading mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Armando Fuentes
              </h2>
              <p className="mt-1 text-sm font-semibold uppercase tracking-wider text-[#D97706]">{t("aboutPage.role")}</p>
            </Reveal>
            <Reveal delay={2}>
              <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="armando-bio">
                {t("aboutPage.bio")}
              </p>
            </Reveal>
            <Reveal delay={3}>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                {t("aboutPage.closingPre")}{" "}
                <a href={SITE.phone1Href} className="font-semibold text-[#0F4C5C] hover:text-[#D97706] transition-colors duration-200" data-testid="about-armando-phone">
                  {SITE.phone1}
                </a>
                .
              </p>
            </Reveal>
            <Reveal delay={4}>
              <div className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="diana-card">
                <div className="flex flex-col border border-[#0F4C5C]/10 bg-[#EEF6FC]">
                  <img
                    src={IMAGES.diana}
                    alt={t("aboutPage.dianaAlt")}
                    className="h-72 w-full object-cover object-top"
                    loading="lazy"
                    data-testid="diana-photo"
                  />
                  <div className="flex-1 p-6">
                  <h3 className="font-heading text-lg font-bold text-slate-900">Diana Morales</h3>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#D97706]">{t("aboutPage.dianaRole")}</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {t("aboutPage.dianaBio")}
                  </p>
                  <div className="mt-4 space-y-2 text-sm">
                    <a href={SITE.phone2Href} className="flex items-center gap-2 font-semibold text-[#0F4C5C] hover:text-[#D97706] transition-colors duration-200" data-testid="diana-phone-link">
                      <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone2}
                    </a>
                    <a href={`mailto:${SITE.emailDiana}`} className="flex items-center gap-2 text-[#0F4C5C] hover:text-[#D97706] transition-colors duration-200" data-testid="diana-email-link">
                      <Mail className="h-4 w-4" aria-hidden="true" /> {SITE.emailDiana}
                    </a>
                  </div>
                  </div>
                </div>
                <div className="flex flex-col border border-[#0F4C5C]/10 bg-white">
                  <img
                    src={IMAGES.exchange}
                    alt={t("aboutPage.exchangeAlt")}
                    className="h-72 w-full object-cover"
                    loading="lazy"
                    data-testid="exchange-building-image"
                  />
                  <div className="flex-1 p-6">
                    <h3 className="font-heading text-lg font-bold text-slate-900">{t("aboutPage.officeHeading")}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">
                      {SITE.address1}
                      <br />
                      {SITE.address2}
                      <br />
                      {SITE.address3}
                    </p>
                    <p className="mt-2 text-sm text-slate-600">{t("common.hours")}</p>
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={5}>
              <Link
                to="/quote"
                className="mt-8 inline-flex items-center gap-2 bg-[#0F4C5C] px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#1A365D]"
                data-testid="about-quote-btn"
              >
                {t("common.requestQuote")} <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
